/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Sagas for Banner component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.0.0
 */

import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';

import {
    makeSelectAuthData,
    makeSelectIsFeatureWithVersionEnabled,
    makeSelectNewsServiceAccessLoggedIn,
} from '../../pages/PageAuth/selectors/authSelectors';
import { IAuthData } from '../../pages/PageAuth/types/authTypes';
import { requestSaga } from '../../sagas/requestSaga';
import { API_HOSTNAME, HIDE_BANNERS, LOAD_BANNERS } from '../app/Api';
import { FeatureNameEnum } from '../role/constants/guard/guardConstants';
import { setCookie } from '../storage/storageActions';
import { makeSelectCookies } from '../storage/storageSelectors';
import { ECookieKey } from '../storage/storageTypes';
import {
    hideAlternativeBannerError,
    hideAlternativeBannerSuccessful,
    hideBannerError,
    hideBannerSuccessful,
    loadBannerError,
    loadBannerSuccessful,
    registrationFailedAlternativeBanner,
} from './bannerActions';
import {
    HIDE_ALTERNATIVE_BANNER,
    HIDE_BANNER,
    LOAD_BANNER,
    REGISTRATION_CLICK_ALTERNATIVE_BANNER,
    REGISTRATION_SHOW_ALTERNATIVE_BANNER,
    defaultBanner,
} from './bannerConstants';
import { makeSelectorBannerId, makeSelectorBannersList, makeSelectorBannerTotalCount } from './bannerSelectors';
import {
    IAlternativeBannerItemResponse,
    THideAlternativeBannerAction,
    TRegistrationClickAlternativeBannerAction,
    TRegistrationShowAlternativeBannerAction,
    IBannerItem,
    EBannerType,
    ICampaignBannerItem,
} from './bannerTypes';

const ERROR = 'Error to load banners';

/*
 * -------------------------------------------
 *           LOAD BANNERS
 * -------------------------------------------
 */

export const getAlternativeBanners = async (url: string, bannerGroupId: number) => {
    const data = await fetch(url + '/show/p/' + bannerGroupId);
    return data.json();
};

function* getClosedAlternativeBanners(): SagaIterator {
    const cookies = yield select(makeSelectCookies());

    return cookies.BANNER_ALTERNATIVE_CLOSED ? decodeURIComponent(cookies.BANNER_ALTERNATIVE_CLOSED).split(',') : [];
}

function* fetchAlternativeBanners(): SagaIterator {
    const { bannerProvider }: IAuthData = yield select(makeSelectAuthData());
    if (!bannerProvider) return;

    const closedBanners = yield call(getClosedAlternativeBanners);

    try {
        const response: { data: IAlternativeBannerItemResponse[] } = yield call(
            getAlternativeBanners,
            bannerProvider.url,
            bannerProvider.bannerGroupId,
        );

        const alternativeData = response.data
            .filter((banner) => {
                return !closedBanners.includes(banner.id.toString());
            })
            .map((banner) => ({
                id: banner.id,
                buttonText: banner.attrs?.button_text ?? '',
                campaignId: banner.id,
                clickHandler: banner.click_handler,
                description: banner.attrs.subtitle,
                imageURL: banner.attrs?.desktop || (banner.attrs?.image_big ?? ''),
                showHandler: banner.show_handler,
                title: banner.attrs.title,
                url: banner.attrs.url,
                type: EBannerType.ALTERNATE,
            }));

        return alternativeData;
    } catch (error: any) {
        return error;
    }
}

export const registrationAltBanner = async (url: string | undefined, postFix?: string): Promise<any> => {
    await fetch(url + (postFix ? postFix : ''));
};

export function* registrationClickAlternativeBannerSaga(
    action: TRegistrationClickAlternativeBannerAction,
): SagaIterator {
    try {
        yield call(registrationAltBanner, action.payload.clickHandler, 'url');
    } catch (error: any) {
        yield put(registrationFailedAlternativeBanner({ error }));
    }
}

export function* loadAlternativeBannerSaga(): SagaIterator {
    const alternativeBanners = yield call(fetchAlternativeBanners);

    if (Array.isArray(alternativeBanners)) {
        yield put(
            loadBannerSuccessful({
                banners: alternativeBanners,
                totalCount: alternativeBanners.length,
            }),
        );
    } else {
        yield put(loadBannerError({ error: alternativeBanners }));
    }
}

export function* loadBannerSaga(): SagaIterator {
    const isNewsServiceUserLoggedIn = yield select(makeSelectNewsServiceAccessLoggedIn());

    if (isNewsServiceUserLoggedIn) return;

    const isCampaignImprovementFeature = yield select(
        makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT, 1, true),
    );

    const auth: IAuthData = yield select(makeSelectAuthData());
    const bannerProvider = auth.bannerProvider;
    const standardBanner = auth.menu.campaigns;

    let banners: ICampaignBannerItem[] | undefined;
    let alternativeBanners: IBannerItem[] | string | undefined;

    try {
        if (!standardBanner && bannerProvider?.url) {
            yield call(loadAlternativeBannerSaga);
        } else {
            const URL = `${API_HOSTNAME}${LOAD_BANNERS}`;
            const data = yield call(requestSaga, URL, 'GET');
            banners = data.banners;
            const totalCount = data.totalCount;

            if (banners) {
                const mappedBanners: IBannerItem[] = banners.map((banner) => {
                    const bannerImageURL = 
                        isCampaignImprovementFeature 
                            ? (banner.desktopImageURL ?? '')
                            : banner.imageURL;

                    return { 
                        id: banner.campaignId,
                        buttonText: banner.buttonText,
                        imageURL: bannerImageURL,
                        type: EBannerType.CAMPAIGN,
                    }
                });

                yield put(
                    loadBannerSuccessful({
                        banners: mappedBanners,
                        totalCount: totalCount,
                    }),
                );

                if (isCampaignImprovementFeature) {
                    alternativeBanners = yield call(fetchAlternativeBanners);

                    if (Array.isArray(alternativeBanners)) {
                        const campaignWithAlternatesBanners = mappedBanners.concat(alternativeBanners);

                        yield put(
                            loadBannerSuccessful({
                                banners: campaignWithAlternatesBanners,
                                totalCount: totalCount,
                            }),
                        );
                    } else {
                        yield put(loadBannerError({ error: alternativeBanners || ERROR }));
                    }
                }
            } else {
                yield put(loadBannerError({ error: ERROR }));
            }
        }
    } catch (error: any) {
        yield put(loadBannerError({ error }));
    } finally {
        const cookies = yield select(makeSelectCookies());
        const bannersClosed = decodeURIComponent(cookies.BANNER_ALTERNATIVE_CLOSED);
        const isDefaultBannerClosed = bannersClosed.split(',').includes(String(defaultBanner.id));
        const totalCount = yield select(makeSelectorBannerTotalCount());
        if (
            isCampaignImprovementFeature && 
            !isDefaultBannerClosed &&
            !totalCount
        ) {
            yield put(
                loadBannerSuccessful({
                    banners: [defaultBanner] as IBannerItem[],
                    totalCount,
                }),
            );
        }
    }
}

/*
 * -------------------------------------------
 *           HIDE BANNERS
 * -------------------------------------------
 */

export function* hideBannerSaga(): SagaIterator {
    const bannerId = yield select(makeSelectorBannerId());

    try {
        yield call(requestSaga, API_HOSTNAME + HIDE_BANNERS(bannerId), 'POST');
        yield put(hideBannerSuccessful());
        yield fork(loadBannerSaga);
    } catch (error: any) {
        yield put(hideBannerError({ error }));
    }
}

/*
 * -------------------------------------------
 *           HIDE ALTERNATIVE BANNERS
 * -------------------------------------------
 */

export function* hideAlternativeBannerSaga(action: THideAlternativeBannerAction): SagaIterator {
    try {
        const alternativeBannersArr: IBannerItem[] = yield select(makeSelectorBannersList());
        const bannerIndex = alternativeBannersArr.findIndex((banner) => banner.id === action.payload.id);
        const cookies = yield select(makeSelectCookies());
        const prevValue = decodeURIComponent(cookies.BANNER_ALTERNATIVE_CLOSED);
        const newValue = alternativeBannersArr[bannerIndex].id.toString().trim();
        const isCampaignImprovementFeature = yield select(
            makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT, 1, true),
        );
        const totalCount = yield select(makeSelectorBannerTotalCount());

        yield put(
            setCookie({
                key: ECookieKey.BANNER_ALTERNATIVE_CLOSED,
                value: `${prevValue}${prevValue ? ',' : ''}${newValue}`,
            }),
        );
        const alternativeBanners = [
            ...alternativeBannersArr.slice(0, bannerIndex),
            ...alternativeBannersArr.slice(bannerIndex + 1),
        ];

        yield put(
            loadBannerSuccessful({
                banners: alternativeBanners,
                totalCount: isCampaignImprovementFeature ? totalCount : alternativeBanners.length,
            }),
        );
        yield put(hideAlternativeBannerSuccessful());
    } catch (error: any) {
        yield put(hideAlternativeBannerError({ error }));
    }
}

export function* registrationShowAlternativeBannerSaga(action: TRegistrationShowAlternativeBannerAction): SagaIterator {
    const cookies = yield select(makeSelectCookies());
    try {
        const prevValue = decodeURIComponent(cookies.BANNER_ALTERNATIVE_SHOWED);
        if (prevValue.search(action.payload.id.toString()) === -1) {
            const newValue = action.payload.id;
            yield put(
                setCookie({
                    key: ECookieKey.BANNER_ALTERNATIVE_SHOWED,
                    value: `${prevValue}${prevValue ? ',' : ''}${newValue}`,
                }),
            );
            yield call(registrationAltBanner, action.payload.showHandler);
        }
    } catch (error: any) {
        yield put(registrationFailedAlternativeBanner({ error }));
    }
}

export function* bannerSagas(): SagaIterator {
    yield all([
        takeEvery(HIDE_BANNER, hideBannerSaga),
        takeEvery(HIDE_ALTERNATIVE_BANNER, hideAlternativeBannerSaga),
        takeEvery(LOAD_BANNER, loadBannerSaga),
        takeEvery(REGISTRATION_CLICK_ALTERNATIVE_BANNER, registrationClickAlternativeBannerSaga),
        takeEvery(REGISTRATION_SHOW_ALTERNATIVE_BANNER, registrationShowAlternativeBannerSaga),
    ]);
}
